// M12 等差數列與等比數列 - 綜合練習數據
// Arithmetic and Geometric Sequences - Comprehensive Exercise Data
// 系統化數據結構，準備 Supabase 後端遷移
// 29個主題，每主題5題，共145題

// ========== 導入所有主題數據 ==========
import { M12_TOPICS_03_10_PROBLEMS, M12_TOPICS_03_10_INFO } from './M12_Topics_03_to_10.js';
import { M12_TOPICS_11_20_PROBLEMS, M12_TOPICS_11_20_INFO } from './M12_Topics_11_to_20.js';
import { M12_TOPICS_21_29_PROBLEMS, M12_TOPICS_21_29_INFO } from './M12_Topics_21_to_29.js';

// ========== 模組基本信息 ==========
export const M12_MODULE_INFO = {
  id: 'M12',
  title: '等差數列與等比數列',
  titleEn: 'Arithmetic and Geometric Sequences',
  description: '學習等差數列與等比數列的性質、公式應用、求和方法，以及在實際問題中的綜合應用',
  totalTopics: 29,
  totalProblems: 145,
  estimatedTime: '20-25小時',
  difficulty: 'intermediate',
  prerequisites: ['M01', 'M02', 'M03'],
  learningObjectives: [
    '掌握等差數列與等比數列的基本概念和性質',
    '熟練運用通項公式和求和公式',
    '理解數列的單調性、有界性和收斂性',
    '應用數列知識解決實際問題',
    '綜合運用等差和等比數列解決複雜問題'
  ],
  applications: [
    '金融複利計算',
    '人口增長模型',
    '設備折舊分析',
    '投資收益計算',
    '物理增長衰減問題'
  ]
};

// ========== 學習主題定義 ==========
export const M12_TOPICS = [
  {
    id: 'M12_01',
    title: '公比求解',
    description: '運用等比數列性質求解公比',
    difficulty: 'basic',
    estimatedMinutes: 30,
    order: 1
  },
  {
    id: 'M12_02',
    title: '公比範圍判斷',
    description: '分析等比數列公比的取值範圍',
    difficulty: 'intermediate',
    estimatedMinutes: 35,
    order: 2
  },
  // 合併所有主題信息
  ...M12_TOPICS_03_10_INFO,
  ...M12_TOPICS_11_20_INFO,
  ...M12_TOPICS_21_29_INFO
];

// ========== 主題 01-02 練習題 ==========
// M12_01: 公比求解 (5題)
export const M12_01_PROBLEMS = [
  {
    id: 'M12_01_P001',
    topicId: 'M12_01',
    type: 'multi_part',
    difficulty: 'basic',
    question: '等比數列公比的基本求解：',
    parts: {
      a: '已知等比數列 a₁ = 3, a₃ = 12',
      b: '求公比 r',
      c: '求 a₅ 的值'
    },
    solution: {
      a: '等比數列通項公式：aₙ = a₁ × rⁿ⁻¹\\n由題意：a₃ = a₁ × r² = 3 × r² = 12',
      b: '解方程：3r² = 12\\nr² = 4\\nr = ±2',
      c: '當r = 2時：a₅ = 3 × 2⁴ = 48\\n當r = -2時：a₅ = 3 × (-2)⁴ = 48\\n所以 a₅ = 48'
    },
    hint: {
      a: '寫出通項公式',
      b: '建立方程求解',
      c: '注意公比可能為負'
    },
    tags: ['等比數列', '公比求解', '通項公式'],
    order: 1
  },
  {
    id: 'M12_01_P002',
    topicId: 'M12_01',
    type: 'multi_part',
    difficulty: 'basic',
    question: '連續三項求公比：',
    parts: {
      a: '等比數列中 a₂ = 6, a₃ = 18, a₄ = 54',
      b: '求公比 r',
      c: '求首項 a₁ 和通項公式'
    },
    solution: {
      a: '等比數列中，相鄰兩項的比值等於公比\\n可以用 aₙ₊₁/aₙ = r 來求解',
      b: 'r = a₃/a₂ = 18/6 = 3\\n驗證：r = a₄/a₃ = 54/18 = 3 ✓',
      c: 'a₁ = a₂/r = 6/3 = 2\\n通項公式：aₙ = 2 × 3ⁿ⁻¹'
    },
    hint: {
      a: '利用相鄰項比值的性質',
      b: '用兩組比值驗證',
      c: '逆推求首項'
    },
    tags: ['等比數列', '相鄰項比值', '首項求解'],
    order: 2
  },
  {
    id: 'M12_01_P003',
    topicId: 'M12_01',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '分數形式的公比求解：',
    parts: {
      a: '等比數列 a₁ = 8, a₄ = 1',
      b: '求公比 r',
      c: '判斷數列的單調性'
    },
    solution: {
      a: '由通項公式：a₄ = a₁ × r³\\n即：1 = 8 × r³',
      b: 'r³ = 1/8\\nr = 1/2',
      c: '因為 0 < r = 1/2 < 1，且 a₁ = 8 > 0\\n所以數列單調遞減'
    },
    hint: {
      a: '用通項公式建立方程',
      b: '求解三次方程',
      c: '根據r的大小判斷單調性'
    },
    tags: ['分數公比', '單調性', '三次方程'],
    order: 3
  },
  {
    id: 'M12_01_P004',
    topicId: 'M12_01',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '含參數的公比求解：',
    parts: {
      a: '等比數列 a₁ = 2, a₂ = 2k, a₃ = 8',
      b: '求參數 k 的值',
      c: '求該數列的前5項'
    },
    solution: {
      a: '等比數列中：a₂² = a₁ × a₃\\n即：(2k)² = 2 × 8',
      b: '4k² = 16\\nk² = 4\\nk = ±2',
      c: '當k = 2時：r = a₂/a₁ = 4/2 = 2\\n前5項：2, 4, 8, 16, 32\\n當k = -2時：r = -4/2 = -2\\n前5項：2, -4, 8, -16, 32'
    },
    hint: {
      a: '使用等比中項性質',
      b: '解二次方程',
      c: '分情況討論k的值'
    },
    tags: ['參數求解', '等比中項', '分情況討論'],
    difficulty: 3,
    order: 4
  },
  {
    id: 'M12_01_P005',
    topicId: 'M12_01',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '複雜條件下的公比求解：',
    parts: {
      a: '等比數列 {aₙ} 滿足 a₁ + a₃ = 10, a₂ + a₄ = 20',
      b: '求首項 a₁ 和公比 r',
      c: '求前n項和公式'
    },
    solution: {
      a: '設首項為a₁，公比為r\\na₁ + a₃ = a₁ + a₁r² = a₁(1 + r²) = 10\\na₂ + a₄ = a₁r + a₁r³ = a₁r(1 + r²) = 20',
      b: '兩式相除：[a₁r(1 + r²)]/[a₁(1 + r²)] = 20/10\\nr = 2\\n代入第一式：a₁(1 + 4) = 10，所以 a₁ = 2',
      c: '前n項和：Sₙ = a₁(rⁿ - 1)/(r - 1) = 2(2ⁿ - 1)/(2 - 1) = 2(2ⁿ - 1) = 2ⁿ⁺¹ - 2'
    },
    hint: {
      a: '建立關於a₁和r的方程組',
      b: '巧妙消元求解',
      c: '使用等比數列求和公式'
    },
    tags: ['方程組', '巧妙消元', '求和公式'],
    difficulty: 4,
    order: 5
  }
];

// M12_02: 公比範圍判斷 (5題)
export const M12_02_PROBLEMS = [
  {
    id: 'M12_02_P001',
    topicId: 'M12_02',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '等比數列收斂性的公比範圍：',
    parts: {
      a: '等比數列 {aₙ} 的無窮級數收斂',
      b: '求公比 r 的取值範圍',
      c: '當 a₁ = 4, r = 1/3 時，求無窮級數的和'
    },
    solution: {
      a: '無窮等比級數 S = a₁ + a₁r + a₁r² + ... 收斂的條件是 |r| < 1',
      b: '公比範圍：-1 < r < 1',
      c: '當 a₁ = 4, r = 1/3 時\\nS = a₁/(1-r) = 4/(1-1/3) = 4/(2/3) = 6'
    },
    hint: {
      a: '回憶無窮級數收斂條件',
      b: '公比絕對值小於1',
      c: '使用無窮等比級數求和公式'
    },
    tags: ['無窮級數', '收斂條件', '公比範圍'],
    difficulty: 2,
    order: 1
  },
  {
    id: 'M12_02_P002',
    topicId: 'M12_02',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '遞增數列的公比範圍：',
    parts: {
      a: '等比數列 {aₙ} 為遞增數列，且 a₁ > 0',
      b: '求公比 r 的取值範圍',
      c: '若還要求 a₁ < 0，公比範圍如何變化？'
    },
    solution: {
      a: '遞增數列要求 aₙ₊₁ > aₙ\\n即 a₁rⁿ > a₁rⁿ⁻¹，化簡得 a₁r(rⁿ⁻¹) > a₁(rⁿ⁻¹)',
      b: '當 a₁ > 0 時，要使數列遞增，需要 r > 1',
      c: '當 a₁ < 0 時，要使數列遞增，需要 0 < r < 1\\n這樣 aₙ = a₁rⁿ⁻¹ 隨n增大而增大（趨向0）'
    },
    hint: {
      a: '分析遞增條件',
      b: '考慮首項為正的情況',
      c: '考慮首項為負的情況'
    },
    tags: ['遞增數列', '公比範圍', '符號討論'],
    difficulty: 3,
    order: 2
  },
  {
    id: 'M12_02_P003',
    topicId: 'M12_02',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '有界數列的公比範圍：',
    parts: {
      a: '等比數列 {aₙ} 滿足 |aₙ| ≤ M（M為常數）',
      b: '若 a₁ = 2，求公比 r 的範圍使得 M = 10',
      c: '分析當 |r| ≥ 1 時數列的性質'
    },
    solution: {
      a: '|aₙ| = |a₁rⁿ⁻¹| = |a₁| × |r|ⁿ⁻¹ ≤ M\\n要使所有項都有界，需要分情況討論',
      b: '當 a₁ = 2 時，|aₙ| = 2|r|ⁿ⁻¹ ≤ 10\\n若 |r| ≥ 1，當n → ∞時，|aₙ| → ∞，不滿足條件\\n所以必須 |r| < 1，即 -1 < r < 1',
      c: '當 |r| > 1 時，數列無界\\n當 |r| = 1 時：r = 1數列為常數列，r = -1數列振盪'
    },
    hint: {
      a: '考慮項的絕對值',
      b: '分析公比絕對值的影響',
      c: '討論邊界情況'
    },
    tags: ['有界數列', '絕對值', '極限分析'],
    difficulty: 4,
    order: 3
  },
  {
    id: 'M12_02_P004',
    topicId: 'M12_02',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '實際問題中的公比範圍：',
    parts: {
      a: '某種藥物在體內的濃度每小時減少20%',
      b: '建立數學模型並確定公比',
      c: '多少小時後濃度降到初始值的1%以下？'
    },
    solution: {
      a: '設初始濃度為C₀，第n小時後濃度為Cₙ\\n每小時減少20%，即保留80%\\nCₙ = C₀ × (0.8)ⁿ',
      b: '這是等比數列，公比 r = 0.8\\n滿足 0 < r < 1，數列遞減且收斂到0',
      c: '要求 Cₙ < 0.01C₀\\nC₀ × (0.8)ⁿ < 0.01C₀\\n(0.8)ⁿ < 0.01\\nn log(0.8) < log(0.01)\\nn > log(0.01)/log(0.8) ≈ 20.6\\n所以21小時後濃度降到1%以下'
    },
    hint: {
      a: '建立遞推關係',
      b: '識別等比數列模型',
      c: '解指數不等式'
    },
    tags: ['實際應用', '藥物濃度', '指數衰減'],
    difficulty: 4,
    order: 4
  },
  {
    id: 'M12_02_P005',
    topicId: 'M12_02',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '複合條件的公比範圍：',
    parts: {
      a: '等比數列 {aₙ} 滿足：a₁ > 0，數列遞減，且前n項和收斂',
      b: '求公比 r 的取值範圍',
      c: '若 a₁ = 1，求無窮級數和的範圍'
    },
    solution: {
      a: '條件分析：\\n1) a₁ > 0\\n2) 數列遞減：aₙ₊₁ < aₙ\\n3) 前n項和收斂：|r| < 1',
      b: '由於 a₁ > 0 且數列遞減，需要 0 < aₙ₊₁ < aₙ\\n即 0 < a₁rⁿ < a₁rⁿ⁻¹，得 0 < r < 1\\n結合收斂條件 |r| < 1，所以 0 < r < 1',
      c: '當 a₁ = 1, 0 < r < 1 時\\nS = a₁/(1-r) = 1/(1-r)\\n因為 0 < r < 1，所以 0 < 1-r < 1\\n因此 S = 1/(1-r) > 1，且當 r → 1⁻ 時，S → +∞\\n所以和的範圍是 (1, +∞)'
    },
    hint: {
      a: '分析每個條件的含義',
      b: '綜合所有約束條件',
      c: '分析和函數的值域'
    },
    tags: ['複合條件', '範圍分析', '函數值域'],
    difficulty: 4,
    order: 5
  }
];

// ========== 合併所有練習題數據 ==========
export const M12_ALL_PROBLEMS = [
  ...M12_01_PROBLEMS,
  ...M12_02_PROBLEMS,
  ...M12_TOPICS_03_10_PROBLEMS,
  ...M12_TOPICS_11_20_PROBLEMS,
  ...M12_TOPICS_21_29_PROBLEMS
];

// ========== 統計信息 ==========
export const M12_STATISTICS = {
  totalProblems: M12_ALL_PROBLEMS.length,
  problemsByDifficulty: {
    basic: M12_ALL_PROBLEMS.filter(p => p.difficulty === 'basic' || p.difficulty === 1 || p.difficulty === 2).length,
    intermediate: M12_ALL_PROBLEMS.filter(p => p.difficulty === 'intermediate' || p.difficulty === 3).length,
    advanced: M12_ALL_PROBLEMS.filter(p => p.difficulty === 'advanced' || p.difficulty === 4).length
  },
  problemsByTopic: M12_TOPICS.reduce((acc, topic) => {
    acc[topic.id] = M12_ALL_PROBLEMS.filter(p => p.topicId === topic.id).length;
    return acc;
  }, {}),
  estimatedTotalTime: M12_TOPICS.reduce((total, topic) => total + topic.estimatedMinutes, 0)
};

// ========== 主要導出 ==========
export default {
  moduleInfo: M12_MODULE_INFO,
  topics: M12_TOPICS,
  problems: M12_ALL_PROBLEMS,
  statistics: M12_STATISTICS,
  // 按主題分組的問題
  problemsByTopic: M12_TOPICS.reduce((acc, topic) => {
    acc[topic.id] = M12_ALL_PROBLEMS.filter(p => p.topicId === topic.id);
    return acc;
  }, {}),
  // 按難度分組的問題
  problemsByDifficulty: {
    basic: M12_ALL_PROBLEMS.filter(p => ['basic', 1, 2].includes(p.difficulty)),
    intermediate: M12_ALL_PROBLEMS.filter(p => ['intermediate', 3].includes(p.difficulty)),
    advanced: M12_ALL_PROBLEMS.filter(p => ['advanced', 4].includes(p.difficulty))
  }
}; 